// src/services/invoiceService.ts

import { supabase } from "../lib/supabase";
import { buildQueryString, fetchBlobWithAuth, fetchWithAuth } from "../lib/api";
import type {
  Invoice,
  InvoiceProperty,
  PropertyContact,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  PreviewInvoiceResponse,
  InvoicePdfPayload,
  InvoiceStatus,
  ClientStageSync,
  OpenInvoiceSummary,
} from "../components/clients/invoiceTypes";
import type { MoneyClient, MoneyInvoice } from "../lib/moneyTruth";
import { INVOICE_CONSTANTS } from "../components/clients/invoiceTypes";

/**
 * Format date as "Month DD, YYYY" for the invoice PDF
 */
export function formatInvoiceDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format date as YYYY-MM-DD for form inputs
 */
export function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Calculate due date (invoice date + 30 days)
 */
export function calculateDueDate(invoiceDateStr: string): string {
  const invoiceDate = new Date(invoiceDateStr);
  const dueDate = new Date(invoiceDate);
  dueDate.setDate(dueDate.getDate() + INVOICE_CONSTANTS.netDays);
  return formatDateForInput(dueDate);
}

/**
 * Calculate commission amount from base rent and percentage
 */
export function calculateCommissionAmount(
  baseRent: number,
  percentage: number
): number {
  return Math.round(baseRent * (percentage / 100) * 100) / 100;
}

/**
 * Build the PDF payload from form data
 */
export function buildPdfPayload(
  invoiceNumber: string,
  request: CreateInvoiceRequest
): InvoicePdfPayload {
  return {
    companyDisplayName: INVOICE_CONSTANTS.companyDisplayName,
    invoiceNumber,
    currency: INVOICE_CONSTANTS.currency,
    balanceDue: request.amount,
    billToName: request.billToName,
    paymentAddressLines: [...INVOICE_CONSTANTS.paymentAddressLines],
    property: {
      name: request.propertyName,
      address: request.propertyAddress,
    },
    invoiceDate: formatInvoiceDate(request.invoiceDate),
    clientName: request.clientName,
    moveInDate: formatInvoiceDate(request.moveInDate),
    agentName: request.agentName,
    items: [
      {
        description: "Resident Referral",
        baseRent: request.baseRent,
        commissionText: request.commissionBasis === "flat" ? "Flat fee" : `${request.commissionPercentage}%`,
        amount: request.amount,
      },
    ],
    total: request.amount,
    termsLines: [...INVOICE_CONSTANTS.termsLines],
    footerLeft: INVOICE_CONSTANTS.footerLeft,
    footerRight: INVOICE_CONSTANTS.footerRight,
  };
}

/**
 * Fetch properties for the dropdown
 */
export async function fetchPropertiesForInvoice(): Promise<InvoiceProperty[]> {
  try {
    const { data, error } = await supabase
      .from("properties")
      .select("id, name, address, property_management, contacts")
      .order("name", { ascending: true });

    if (error) throw error;

    return (data || []).map((p) => ({
      id: p.id,
      name: p.name || "Unknown Property",
      address: p.address || "",
      property_management: p.property_management,
      contacts: parseContacts(p.contacts),
    }));
  } catch (error) {
    console.error("Error fetching properties:", error);
    return [];
  }
}

/**
 * Parse contacts from JSONB column
 */
function parseContacts(contacts: unknown): PropertyContact[] {
  if (!contacts) return [];
  if (Array.isArray(contacts)) {
    return contacts
      .filter((c) => c && typeof c === "object" && c.email)
      .map((c, index) => ({
        id: c.id || `contact-${Date.now()}-${index}`,
        name: c.name || "Unknown",
        email: c.email,
        role: c.role || undefined,
      }));
  }
  return [];
}

/**
 * Get the next invoice number (for preview purposes)
 */
export async function getNextInvoiceNumber(): Promise<string> {
  try {
    const { data, error } = await supabase.rpc("get_next_invoice_number");

    if (error) {
      // Fallback: query the sequence directly or estimate
      console.warn("Could not get next invoice number via RPC:", error);
      return "INV-XXXXX";
    }

    return data || "INV-XXXXX";
  } catch (error) {
    console.error("Error getting next invoice number:", error);
    return "INV-XXXXX";
  }
}

/**
 * Preview invoice PDF without saving
 * Note: Using /invoices/preview because fetchWithAuth already adds /api prefix
 */
export async function previewInvoice(
  request: CreateInvoiceRequest
): Promise<PreviewInvoiceResponse> {
  try {
    const response = await fetchWithAuth<{
      success: boolean;
      pdf_base64?: string;
      invoice_number?: string;
      error?: string;
    }>("/invoices/preview", {
      method: "POST",
      body: JSON.stringify(request),
    });

    return {
      success: response.success,
      pdfBase64: response.pdf_base64,
      invoiceNumber: response.invoice_number,
      error: response.error,
    };
  } catch (error: any) {
    console.error("Error previewing invoice:", error);
    return {
      success: false,
      error: error.message || "Failed to generate preview",
    };
  }
}

/**
 * Create and send invoice
 * Note: Using /invoices/send because fetchWithAuth already adds /api prefix
 */
export async function createAndSendInvoice(
  request: CreateInvoiceRequest
): Promise<CreateInvoiceResponse> {
  try {
    const response = await fetchWithAuth<{
      success: boolean;
      invoice?: Invoice;
      pdf_url?: string;
      error?: string;
      requested_recipient_count?: number;
      submitted_recipient_count?: number;
      blocked_cc_count?: number;
      delivery_state?: CreateInvoiceResponse["deliveryState"];
      send_action_id?: string;
    }>("/invoices/send", {
      method: "POST",
      body: JSON.stringify(request),
    });

    return {
      success: response.success,
      invoice: response.invoice,
      pdfUrl: response.pdf_url,
      error: response.error,
      requestedRecipientCount: response.requested_recipient_count,
      submittedRecipientCount: response.submitted_recipient_count,
      blockedCcCount: response.blocked_cc_count,
      deliveryState: response.delivery_state,
      sendActionId: response.send_action_id,
    };
  } catch (error: any) {
    console.error("Error creating invoice:", error);
    return {
      success: false,
      error: error.message || "Failed to send invoice",
    };
  }
}

export async function downloadInvoicePdf(invoiceId: string): Promise<Blob> {
  return fetchBlobWithAuth(`/invoices/${encodeURIComponent(invoiceId)}/pdf`);
}

export async function exportInvoicesCsv(filters: {
  from?: string;
  to?: string;
  status?: InvoiceStatus | "";
  clientId?: string;
} = {}): Promise<Blob> {
  const query = buildQueryString({
    from: filters.from || undefined,
    to: filters.to || undefined,
    status: filters.status || undefined,
    client_id: filters.clientId || undefined,
  });
  return fetchBlobWithAuth(`/invoices/export.csv${query}`);
}

/**
 * Update invoice status through the backend and return the UPDATED row.
 *
 * PKT-0911: this used to write straight to Supabase and only checked `error`.
 * RLS lets only the owner/assignee UPDATE, and a filtered-out row is a 2xx
 * with no error — so a non-owner's "mark paid" reported success while nothing
 * changed. The backend route (PATCH /invoices/{id}/status) checks authority,
 * 404s on a zero-row update, and writes the activity row itself.
 *
 * Resolves ONLY with a row that shows the requested status; anything else
 * throws, so a caller cannot mistake "the request returned" for "it saved".
 */
export async function updateInvoiceStatus(
  invoiceId: string,
  status: InvoiceStatus
): Promise<Invoice & { client_stage_sync?: ClientStageSync }> {
  const row = await fetchWithAuth<(Invoice & { client_stage_sync?: ClientStageSync }) | null>(
    `/invoices/${encodeURIComponent(invoiceId)}/status`,
    { method: "PATCH", body: JSON.stringify({ status }) }
  );
  if (!row || row.id !== invoiceId || row.status !== status) {
    throw new Error("Status was not saved. Refresh and try again.");
  }
  return row;
}

/**
 * PKT-0916 · the client's invoices that are still owed (sent / overdue), for
 * the "Mark INV-xxxx paid too?" prompt. THROWS on a failed read — the caller
 * decides what "could not look" means; it is never returned as "none".
 */
export async function fetchOpenInvoicesForClient(
  clientId: string
): Promise<OpenInvoiceSummary[]> {
  const { data, error } = await supabase
    .from("invoices")
    .select("id, invoice_number, status, amount, property_name, due_date")
    .eq("client_id", clientId)
    .in("status", ["sent", "overdue"])
    .order("due_date", { ascending: true });
  if (error) throw error;
  return (data || []) as OpenInvoiceSummary[];
}

/**
 * PKT-0916 (C) · the rows the Dashboard money panel derives from: every
 * invoice and client the viewer can read (RLS: owner/assignee, or
 * admin/manager). THROWS on a failed read so the panel can say "unavailable"
 * instead of showing $0.
 */
export async function fetchMoneyViewData(): Promise<{
  invoices: MoneyInvoice[];
  clients: MoneyClient[];
}> {
  const [inv, cl] = await Promise.all([
    supabase
      .from("invoices")
      .select("id, client_id, invoice_number, status, amount, due_date, paid_at")
      .in("status", ["sent", "overdue", "paid", "cancelled", "failed"]),
    supabase
      .from("clients")
      .select("id, status, move_in_date, is_test_client, is_synthetic"),
  ]);
  if (inv.error) throw inv.error;
  if (cl.error) throw cl.error;
  return {
    invoices: (inv.data || []) as MoneyInvoice[],
    clients: (cl.data || []) as MoneyClient[],
  };
}

/**
 * Fetch invoices for a client
 */
export async function fetchClientInvoices(
  clientId: string
): Promise<Invoice[]> {
  try {
    const { data, error } = await supabase
      .from("invoices")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error("Error fetching client invoices:", error);
    return [];
  }
}

// logInvoiceActivity was removed 2026-08-01: zero callers, and it accepted an arbitrary
// activity_type it wrote straight to activities — a contact-type write site waiting to be
// wired up. The backend (invoices.py) already logs invoice activities server-side.

export default {
  fetchPropertiesForInvoice,
  getNextInvoiceNumber,
  previewInvoice,
  createAndSendInvoice,
  downloadInvoicePdf,
  exportInvoicesCsv,
  updateInvoiceStatus,
  fetchOpenInvoicesForClient,
  fetchMoneyViewData,
  fetchClientInvoices,
  formatInvoiceDate,
  formatDateForInput,
  calculateDueDate,
  calculateCommissionAmount,
  buildPdfPayload,
};
